import { useCallback, useEffect, useReducer, useState, type FormEvent } from "react";
import { DeviceReadiness } from "../call/DeviceReadiness";
import { formatPhoneDisplay, normalizePhone } from "../../services/phoneNumber";
import {
  cancelSession, clearStoredSession, getSession, markReady, readStoredSession, requestOtp, setGuestPhone, verifyOtp
} from "../../services/publicSession";
import { useI18n } from "../../i18n/localization";
import {
  initialPublicFlow, isInvitationGone, isSessionGone, publicErrorKey, publicFlowReducer, sanitizeOtpInput, type PublicErrorKey
} from "./publicFlow";
import { PublicCallStage } from "./PublicCallStage";
import { FieldError, LinkButton, PrimaryButton, StepCard, label, muted, textInput } from "./PublicShell";
import { usePublicLiveSync } from "./usePublicLiveSync";

// Public site root ("/"): welcome + phone → OTP → other party's phone → device readiness →
// waiting (copy invitation link) → the call. Each step is server-validated; the OTP value is
// never displayed, hinted, prefilled or stored.
export function PublicEntry() {
  const { t } = useI18n();
  const [state, dispatch] = useReducer(publicFlowReducer, initialPublicFlow);
  const [token, setToken] = useState<string | null>(() => readStoredSession("initiator")?.token ?? null);
  const [busy, setBusy] = useState(false);
  const [restoreFailed, setRestoreFailed] = useState(false);

  const signOut = useCallback(() => {
    clearStoredSession("initiator");
    setToken(null);
    dispatch({ type: "signedOut" });
  }, []);

  // Resume a session restored from this tab (refresh / reconnect).
  const restore = useCallback(() => {
    const stored = readStoredSession("initiator");
    if (!stored) { dispatch({ type: "signedOut" }); return; }
    setRestoreFailed(false);
    getSession(stored.token)
      .then((view) => dispatch({ type: "view", view }))
      .catch((e: unknown) => { if (isSessionGone(e)) signOut(); else setRestoreFailed(true); });
  }, [signOut]);
  useEffect(restore, [restore]);

  const { endCall } = usePublicLiveSync({ token, screen: state.screen, dispatch, onSessionGone: signOut });

  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    dispatch({ type: "error", error: null });
    try {
      await work();
    } catch (e) {
      if (isSessionGone(e)) signOut();
      else dispatch({ type: "error", error: publicErrorKey(e) });
    } finally {
      setBusy(false);
    }
  };

  const errorText = state.error ? t.pub.errors[state.error] : null;
  const screen = state.screen;

  // Ready → the one invitation + call is created server-side (idempotent on repeat/refresh).
  // A closed/expired invitation answers 410: show its state instead of a generic error.
  const sendReady = () => void run(async () => {
    if (!token) return;
    try {
      dispatch({ type: "view", view: await markReady(token) });
    } catch (e) {
      if (!isInvitationGone(e)) throw e;
      dispatch({ type: "view", view: await getSession(token) });
    }
  });

  switch (screen.kind) {
    case "loading":
      return (
        <StepCard title={t.pub.welcomeTitle} labelledBy="pub-loading">
          <p role="status" style={muted}>{restoreFailed ? t.pub.errors.unavailable : t.pub.loading}</p>
          {restoreFailed && <PrimaryButton type="button" onClick={restore}>{t.pub.continue}</PrimaryButton>}
        </StepCard>
      );

    case "phone":
      return <PhoneStep busy={busy} error={errorText} onSubmit={(raw, display) => run(async () => {
        const r = await requestOtp(raw);
        dispatch({ type: "otpRequested", challengeId: r.challengeId, phoneDisplay: display, delivery: r.delivery === "whatsapp" ? "whatsapp" : "mock" });
      })} onInvalid={() => dispatch({ type: "error", error: "invalidPhone" })} />;

    case "otp":
      return <OtpStep phoneDisplay={screen.phoneDisplay} delivery={screen.delivery} busy={busy} error={errorText}
        onBack={() => dispatch({ type: "changePhone" })}
        onMissing={() => dispatch({ type: "error", error: "codeRequired" })}
        onSubmit={(code) => run(async () => {
          const s = await verifyOtp(screen.challengeId, code);
          setToken(s.token);
          dispatch({ type: "view", view: await getSession(s.token) });
        })} />;

    case "guestPhone":
      return <GuestPhoneStep displayName={state.view?.displayName ?? ""} busy={busy} error={errorText}
        onStartOver={signOut}
        onInvalid={(key) => dispatch({ type: "error", error: key })}
        onSubmit={(raw) => run(async () => {
          if (token) dispatch({ type: "view", view: await setGuestPhone(token, raw) });
        })} />;

    case "readiness":
      return (
        <DeviceReadiness role="field" continueLabel={t.pub.readyAction} onReady={(prefs) => {
          dispatch({ type: "devicesReady", prefs });
          sendReady();
        }} />
      );

    default:
      return (
        <PublicCallStage screen={screen} view={state.view} prefs={state.prefs!}
          error={errorText} onRetry={sendReady}
          onPrefs={(prefs) => dispatch({ type: "devicesReady", prefs })}
          onCancel={() => void run(async () => {
            if (!token) return;
            dispatch({ type: "view", view: await cancelSession(token) });
            clearStoredSession("initiator");
            setToken(null);
          })}
          onEnd={(callId) => void (async () => {
            if (await endCall(callId)) return; // callEnded arrives over the hub
            if (token) await cancelSession(token).then((view) => dispatch({ type: "view", view })).catch(() => undefined);
          })()}
          onStartOver={signOut} />
      );
  }
}

function PhoneStep({ busy, error, onSubmit, onInvalid }: {
  busy: boolean; error: string | null;
  onSubmit: (raw: string, display: string) => void;
  onInvalid: () => void;
}) {
  const { t } = useI18n();
  const [phone, setPhone] = useState("");
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const normalized = normalizePhone(phone);
    if (!normalized) { onInvalid(); return; }
    onSubmit(phone.trim(), formatPhoneDisplay(normalized));
  };
  return (
    <StepCard title={t.pub.welcomeTitle} labelledBy="pub-phone">
      <p style={muted}>{t.pub.welcomeText}</p>
      <form onSubmit={submit} noValidate>
        <label style={label}>
          {t.pub.phoneLabel}
          {/* Scoped placeholder colour (inline styles cannot target ::placeholder). */}
          <style>{`.vc-pub-phone-input::placeholder { color: #94a3b8; opacity: 1; }`}</style>
          <input type="tel" inputMode="tel" autoComplete="tel" dir="ltr" value={phone} autoFocus required
            className="vc-pub-phone-input" placeholder={t.pub.phonePlaceholder}
            onChange={(e) => setPhone(e.target.value)} aria-invalid={!!error} style={{ ...textInput, color: "#1e2a3a" }} />
        </label>
        <FieldError message={error} />
        <PrimaryButton type="submit" busy={busy}>{busy ? t.pub.checking : t.pub.continue}</PrimaryButton>
      </form>
    </StepCard>
  );
}

function OtpStep({ phoneDisplay, delivery, busy, error, onSubmit, onBack, onMissing }: {
  phoneDisplay: string; delivery: "whatsapp" | "mock"; busy: boolean; error: string | null;
  onSubmit: (code: string) => void; onBack: () => void; onMissing: () => void;
}) {
  const { t } = useI18n();
  const [code, setCode] = useState("");
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!code) { onMissing(); return; }
    const submitted = code;
    setCode(""); // never keep the code around after an attempt
    onSubmit(submitted);
  };
  return (
    <StepCard title={t.pub.otpTitle} labelledBy="pub-otp">
      <p style={muted}>
        {delivery === "whatsapp" ? t.pub.otpTextWhatsApp : t.pub.otpTextMock}{" "}
        <strong dir="ltr" style={{ unicodeBidi: "isolate" }}>{phoneDisplay}</strong>
      </p>
      <form onSubmit={submit} noValidate>
        <label style={label}>
          {t.pub.otpLabel}
          {/* Masked numeric entry: supports typing, paste and the mobile one-time-code keyboard. */}
          <input type="password" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]*" dir="ltr"
            value={code} autoFocus required maxLength={8}
            onChange={(e) => setCode(sanitizeOtpInput(e.target.value))} aria-invalid={!!error} style={{ ...textInput, letterSpacing: 6 }} />
        </label>
        <FieldError message={error} />
        <PrimaryButton type="submit" busy={busy}>{busy ? t.pub.checking : t.pub.verify}</PrimaryButton>
      </form>
      <LinkButton onClick={onBack}>{t.pub.changePhone}</LinkButton>
    </StepCard>
  );
}

function GuestPhoneStep({ displayName, busy, error, onSubmit, onInvalid, onStartOver }: {
  displayName: string; busy: boolean; error: string | null;
  onSubmit: (raw: string) => void; onInvalid: (key: PublicErrorKey) => void; onStartOver: () => void;
}) {
  const { t } = useI18n();
  const [phone, setPhone] = useState("");
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!normalizePhone(phone)) { onInvalid("invalidPhone"); return; }
    onSubmit(phone.trim());
  };
  return (
    <StepCard title={t.pub.guestTitle} labelledBy="pub-guest">
      {displayName && <p style={{ ...muted, margin: "0 0 8px" }}>{t.pub.signedInAs} <strong>{displayName}</strong></p>}
      <p style={muted}>{t.pub.guestText}</p>
      <form onSubmit={submit} noValidate>
        <label style={label}>
          {t.pub.guestLabel}
          <input type="tel" inputMode="tel" autoComplete="off" dir="ltr" value={phone} autoFocus required
            onChange={(e) => setPhone(e.target.value)} aria-invalid={!!error} style={textInput} />
        </label>
        <FieldError message={error} />
        <PrimaryButton type="submit" busy={busy}>{t.pub.guestContinue}</PrimaryButton>
      </form>
      <LinkButton onClick={onStartOver}>{t.pub.startOver}</LinkButton>
    </StepCard>
  );
}
